import { useEffect, useState } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProtectedRoute() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null
  );
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    // Skip if user passed from AuthCallback
    if (location.state?.user) {
      setIsAuthenticated(true);
      setUser(location.state.user);
      return;
    }
    // CRITICAL (per Emergent Auth playbook): if we're returning from OAuth and the
    // URL still contains session_id=, AuthCallback is handling the exchange. Skip
    // the /auth/me check so we don't 401 before the cookie is set.
    if (window.location.hash?.includes("session_id=")) {
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        // Only treat genuine 401s as "logged out". Transient/network errors keep
        // the user where they are; React Router will retry on next navigation.
        if (error?.response?.status === 401) {
          localStorage.removeItem("session_token");
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();
  }, [location.state]);

  // Still checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-[hsl(var(--primary))] mx-auto mb-4" />
          <p className="text-[hsl(var(--muted-foreground))]">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Role-based redirects (admin-only paths bounce non-admins back to /dashboard)
  const isAdmin = user?.role === "admin" || user?.role === "org_admin" || user?.role === "venue_manager" || user?.role === "system_admin";
  const adminOnlyPaths = ["/cocktails", "/upload", "/staff", "/create-test", "/competitions", "/history"];

  if (!isAdmin && adminOnlyPaths.includes(location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Authenticated - render child routes with user context
  return <Outlet context={{ user }} />;
}
